import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'

import { Dialog, Text, Button, Form, Dropdown } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import {
  fetchUserIncomePlans,
  selectUserIncomePlanById,
  selectUserIncomePlans,
} from '@/features/income-planning/incomePlanning.slice'
import { getFieldNames, makeReqStringSchema } from '@/base'
import { useReselector } from '@/utils/sharedHooks'
import { PlanInfoCard } from '@/features/income-planning/PlanInfoCard'

export const CreateFromExistingOrEditDialog = ({
  createFromExisting,
}: {
  createFromExisting: boolean
}) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const plans = useReselector(selectUserIncomePlans)

  const planOptions = useMemo(
    () => Object.values(plans).map((p) => ({ value: p.id, text: p.planName })),
    [plans]
  )

  useEffect(() => {
    dispatch(fetchUserIncomePlans())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      selectedPlanId: null as number | null,
    },
    onSubmit: ({ selectedPlanId }) => {
      if (!selectedPlanId) {
        return
      }

      navigate(
        createFromExisting
          ? `/income-planning/create-plan?fromPlan=${selectedPlanId}`
          : `/income-planning/edit-plan/${selectedPlanId}`
      )
    },
  })

  const { submitForm, values } = formik
  const fieldNames = getFieldNames(formik)

  const selectedPlan = useReselector(
    selectUserIncomePlanById,
    values.selectedPlanId
  )

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <Dialog.Trigger asChild>
        <Dropdown.Item
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpen(true)
          }}
        >
          <Text variant={TypographyStyles.BODY_SM}>
            {createFromExisting
              ? 'Create from existing template'
              : 'Edit existing template'}
          </Text>
        </Dropdown.Item>
      </Dialog.Trigger>

      <Dialog.Content onDismiss={() => setOpen(false)}>
        <FormikProvider value={formik}>
          <Dialog.Title>
            {createFromExisting ? 'Create from existing template' : 'Edit Plan'}
          </Dialog.Title>
          <Text variant={TypographyStyles.BODY_SM} className="mt-2">
            {createFromExisting
              ? 'Select which plan you’d like to use.'
              : 'Select which plan you’d like to update'}
          </Text>

          <div className="mt-6">
            <Form.Select
              label="Select plan"
              name={fieldNames.selectedPlanId}
              options={planOptions}
              schema={makeReqStringSchema()}
            />
          </div>

          {selectedPlan && (
            <PlanInfoCard showComparePlan={false} sentPlan={selectedPlan} />
          )}

          <Dialog.Footer className="mt-6">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitForm}>View</Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
